import { useEffect, useRef, useState } from "react";
import { optionsApi } from "../../services/optionsApi";
import styles from "./SelectOrAdd.module.css";

const ADD_NEW = "__add_new__";

/**
 * A dropdown of known values with an inline "add new" escape hatch (#189).
 *
 * The product rule is that users pick rather than type wherever a value
 * repeats, so this is deliberately a native <select> rather than a custom
 * listbox: it gets the platform picker on mobile browsers, keyboard support
 * and screen-reader semantics for free, and none of that is worth
 * reimplementing badly.
 *
 * Choosing "+ Add new..." swaps in a text input. On save the value is POSTed
 * so it is remembered for next time, then selected. That POST is idempotent
 * server-side, so a double-submit on a bad connection is harmless.
 *
 * Props:
 *   listKey   which option list to load (see DEFAULT_OPTIONS in options.py)
 *   value     currently selected value (a string, not an id -- consuming
 *             records store the text, so history survives a renamed option)
 *   onChange  called with the newly selected string
 *   options   optional: supply the list yourself instead of fetching, for
 *             callers that already loaded every list via optionsApi.listAll()
 */
export function SelectOrAdd({
  listKey,
  value,
  onChange,
  label,
  options: providedOptions,
  disabled = false,
  required = false,
  id,
  placeholder = "Select...",
}) {
  const [options, setOptions] = useState(providedOptions || []);
  const [loading, setLoading] = useState(!providedOptions);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const selectId = id || `select-${listKey}`;

  useEffect(() => {
    if (providedOptions) {
      setOptions(providedOptions);
      setLoading(false);
      return;
    }
    // Guards against a stale response overwriting a newer one if listKey
    // changes while a request is in flight.
    let cancelled = false;
    setLoading(true);
    optionsApi
      .list(listKey)
      .then((data) => {
        if (!cancelled) setOptions(data.options || []);
      })
      .catch(() => {
        // A failed load must not trap the user in an empty dropdown with no
        // way forward -- surface it and leave "add new" usable.
        if (!cancelled) setError("Could not load choices");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [listKey, providedOptions]);

  useEffect(() => {
    if (adding) inputRef.current?.focus();
  }, [adding]);

  function handleSelect(e) {
    const next = e.target.value;
    if (next === ADD_NEW) {
      setDraft("");
      setError(null);
      setAdding(true);
      return;
    }
    onChange(next);
  }

  async function commitDraft() {
    const trimmed = draft.trim();
    if (!trimmed) {
      setAdding(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await optionsApi.create(listKey, { value: trimmed });
      setOptions((prev) =>
        prev.some((o) => o.value === created.value) ? prev : [...prev, created]
      );
      onChange(created.value);
      setAdding(false);
    } catch {
      // Keep the typed text and stay in add mode -- retyping a value the user
      // just entered, in a field, is exactly the friction this control exists
      // to remove.
      setError("Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      commitDraft();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setAdding(false);
      setError(null);
    }
  }

  if (adding) {
    return (
      <div className={styles.wrapper}>
        {label && (
          <label className={styles.label} htmlFor={`${selectId}-new`}>
            {label}
          </label>
        )}
        <div className={styles.addRow}>
          <input
            id={`${selectId}-new`}
            ref={inputRef}
            className={styles.input}
            type="text"
            value={draft}
            maxLength={100}
            disabled={saving}
            placeholder="New value"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button
            type="button"
            className={styles.addButton}
            onClick={commitDraft}
            disabled={saving || !draft.trim()}
          >
            {saving ? "Saving..." : "Save"}
          </button>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={() => {
              setAdding(false);
              setError(null);
            }}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {label && (
        <label className={styles.label} htmlFor={selectId}>
          {label}
        </label>
      )}
      <select
        id={selectId}
        className={styles.select}
        value={value || ""}
        onChange={handleSelect}
        disabled={disabled || loading}
        required={required}
      >
        <option value="" disabled={required}>
          {loading ? "Loading..." : placeholder}
        </option>
        {/* A value saved earlier can be absent from the list if it was since
            retired. Show it anyway, or editing an old record would silently
            blank a field the user never touched.

            SAID OUT LOUD since #417 gave people a way to retire one.
            Reported: "if I had the specific type selected the last time I
            was in Add Equipment the drop down remembers it until I change
            it and then I'm not able to select it from the options."

            That is the intended behaviour -- it was put away -- but it
            looked like any other option, so losing it read as the list
            dropping something rather than as a consequence of a choice
            made a minute earlier. Labelling it costs nothing and turns a
            disappearing act into an explanation. */}
        {value && !options.some((o) => o.value === value) && (
          <option value={value}>{value} (put away)</option>
        )}
        {options.map((o) => (
          <option key={o.option_id ?? `default-${o.value}`} value={o.value}>
            {o.value}
          </option>
        ))}
        <option value={ADD_NEW}>+ Add new...</option>
      </select>
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
